import { useState } from 'react'
import { toast } from 'sonner'

import { CarrierPicker } from '@/components/CarrierPicker'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { parseError } from '@/lib/errors'
import {
  CARRIER_ADMITTED_STATUS_LABELS,
  CarrierAdmittedStatus,
  useInsuranceCarriers,
  useMergeCarrier,
} from '@/models/insuranceCarrier'

// Radix treats an empty string as "no selection" and refuses to render an item with one.
const NONE = 'none'

interface MergeCarriersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Folds a duplicate carrier into the one to keep — every row moves, then it is deleted. */
export function MergeCarriersDialog({ open, onOpenChange }: MergeCarriersDialogProps) {
  const merge = useMergeCarrier()
  // The same hook the pickers read, so a chosen id always resolves to a record here.
  const { carriers } = useInsuranceCarriers()
  const [sourceId, setSourceId] = useState('')
  const [targetId, setTargetId] = useState('')
  const [admittedStatus, setAdmittedStatus] = useState<CarrierAdmittedStatus | ''>('')

  const source = carriers.find((carrier) => carrier.id === sourceId)
  const target = carriers.find((carrier) => carrier.id === targetId)
  const sameName = source && target && source.name.trim().toLowerCase() === target.name.trim().toLowerCase()

  function reset() {
    setSourceId('')
    setTargetId('')
    setAdmittedStatus('')
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  function handleMerge() {
    if (!source || !target) return
    const sourceName = source.name
    const targetName = target.name
    merge.mutate(
      { targetId: target.id, sourceId: source.id, admittedStatus },
      {
        onSuccess: () => {
          toast.success(`${sourceName} merged into ${targetName}`)
          handleOpenChange(false)
        },
        onError: (error) => toast.error(parseError(error)),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge carriers</DialogTitle>
          <DialogDescription>
            Fold a duplicate carrier into the one you want to keep. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="merge-source">Carrier to merge away</Label>
            <CarrierPicker
              id="merge-source"
              value={sourceId || null}
              placeholder="Pick a carrier"
              allowClear={false}
              onChange={(next) => {
                setSourceId(next ?? '')
                if (next === targetId) setTargetId('')
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="merge-target">Merge into</Label>
            <CarrierPicker
              id="merge-target"
              value={targetId || null}
              placeholder="Pick the carrier to keep"
              allowClear={false}
              // Dropping the source makes a self-merge unreachable, not just rejected.
              exclude={sourceId ? [sourceId] : undefined}
              onChange={(next) => setTargetId(next ?? '')}
            />
          </div>

          {source && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="merge-admitted-status">Insurance type</Label>
              <Select
                value={admittedStatus || NONE}
                onValueChange={(next) =>
                  setAdmittedStatus(next === NONE ? '' : (next as CarrierAdmittedStatus))
                }
              >
                <SelectTrigger id="merge-admitted-status" className="w-full">
                  <SelectValue placeholder="Unknown" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Unknown</SelectItem>
                  {Object.values(CarrierAdmittedStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {CARRIER_ADMITTED_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                Only for a brand&apos;s separate paper, like Chubb European Group (APEX). Offers that come in
                under the old name get stamped with it. Leave it alone for a plain duplicate.
              </p>
            </div>
          )}

          {source && target && (
            <Banner variant="warning" small>
              <p data-testid="merge-consequence">
                Every quote, policy, contact and lead on <strong>{source.name}</strong> moves to{' '}
                <strong>{target.name}</strong>. {source.name} is then deleted
                {sameName
                  ? '.'
                  : `, and its name is kept as an alias so a vendor sending it still resolves to ${target.name}.`}
              </p>
              <p className="mt-1 text-xs">
                What was filled in on {source.name} — its logo, rating, notes and the rest of its details — is
                not copied. Put anything worth keeping on {target.name} first.
              </p>
            </Banner>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!source || !target}
            loading={merge.isPending ? 'Merging…' : undefined}
            onClick={handleMerge}
          >
            Merge carriers
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
